import { readSetting, writeSetting } from '../../infrastructure/storage/localStore';

/**
 * Which plants this device has already been told about (SPEC-016).
 *
 * Kept TWICE, in two storage systems, because two different runtimes need it and
 * neither can read the other's:
 *
 *   - Web Storage is the app's own copy. It is synchronous, it is where every
 *     other preference lives, and it is the only one that works in every engine.
 *   - Cache Storage is a MIRROR for the service worker, which has no access to
 *     Web Storage at all and needs the same list to answer a `periodicsync`
 *     while no page is open.
 *
 * The mirror is written after the app's copy, so a failed mirror costs the
 * background check and nothing else. It carries the locale too: a worker waking
 * with no page open has no other way to know which of four locales to write the
 * notification in.
 *
 * It is read BOTH ways. The worker updates only the mirror — it cannot reach Web
 * Storage — so a page that read only its own copy would re-diff a plant the
 * worker had already announced in the background and announce it a second time.
 * The union of the two is what this device has actually been told about.
 */

export const SEEN_PLANTS_KEY = 'plants.seen';
/** When the catalogue was last read. Shown in the drawer's status list (SPEC-018). */
export const LAST_CHECKED_KEY = 'plants.lastCheckedAt';

/**
 * Shared verbatim with `assets/service-worker.js`, which cannot import from
 * `src/`. `src/pwaArtifact.test.js` asserts the two spellings still agree —
 * a rename on one side alone would not fail anything else until a background
 * notification quietly stopped arriving.
 *
 * Deliberately NOT prefixed `plant-habb-me-`: that prefix is what the service
 * worker's activate sweep and `appUpdate`'s `clearAppCaches` both delete by, and
 * this outlives every build.
 */
export const PLANT_WATCH_CACHE_NAME = 'plant-watch-state';
export const PLANT_WATCH_STATE_URL = '/__plant-watch-state';

function parsePlantIds(value) {
    return Array.isArray(value) ? value.filter((id) => typeof id === 'string' && id !== '') : [];
}

/**
 * `hasSeenCatalogue` is the ABSENCE of the key, not an empty list.
 *
 * A device that has never read a catalogue must be seeded silently; a device
 * whose catalogue genuinely went empty must not be. Only the missing key can
 * tell those apart, which is why this is not `plantIds.length > 0`.
 */
export function readSeenPlants() {
    const raw = readSetting(SEEN_PLANTS_KEY);

    if (raw === null || raw === undefined) {
        return { plantIds: [], hasSeenCatalogue: false };
    }

    try {
        return { plantIds: parsePlantIds(JSON.parse(raw)), hasSeenCatalogue: true };
    } catch (error) {
        // A half-written or foreign value is not a catalogue this device has
        // seen. Seeding again is silent; treating it as seen would announce the
        // whole catalogue on the next read.
        return { plantIds: [], hasSeenCatalogue: false };
    }
}

export function readLastCheckedAt() {
    const parsed = Number.parseInt(readSetting(LAST_CHECKED_KEY), 10);

    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function writeLastCheckedAt(now = Date.now()) {
    return writeSetting(LAST_CHECKED_KEY, String(now));
}

export function writeSeenPlants(plantIds) {
    return writeSetting(SEEN_PLANTS_KEY, JSON.stringify(parsePlantIds(plantIds)));
}

function getCacheStorage(globalObject = typeof globalThis === 'undefined' ? null : globalThis) {
    try {
        return globalObject && globalObject.caches ? globalObject.caches : null;
    } catch (error) {
        // Cache Storage throws rather than returning null in some private modes.
        return null;
    }
}

/**
 * Hand the worker the same list, plus the locale to say it in.
 *
 * Best-effort by construction: every failure path returns false and none of them
 * throw. Losing the mirror loses the background check on Chromium and nothing
 * else — the app's own check still runs on every open.
 */
/**
 * What the worker recorded, or null where there is nothing to read.
 *
 * Best-effort like its counterpart: Cache Storage is absent in some private
 * modes and in jsdom, and every failure path here reads as "the worker has told
 * this device about nothing", which is the safe direction — it can only cause a
 * plant to be announced, never suppressed.
 */
export async function readMirroredPlantWatchState({ globalObject } = {}) {
    const cacheStorage = getCacheStorage(globalObject);

    if (!cacheStorage || typeof cacheStorage.open !== 'function') {
        return null;
    }

    try {
        const cache = await cacheStorage.open(PLANT_WATCH_CACHE_NAME);
        const response = await cache.match(PLANT_WATCH_STATE_URL);

        if (!response) {
            return null;
        }

        const state = await response.json();

        return state && typeof state === 'object'
            ? {
                localeCode: typeof state.localeCode === 'string' ? state.localeCode : '',
                seenPlantIds: parsePlantIds(state.seenPlantIds),
                isMuted: state.isMuted === true,
            }
            : null;
    } catch (error) {
        return null;
    }
}

export async function mirrorPlantWatchState({
    localeCode = '',
    seenPlantIds = [],
    isMuted = false,
    lastCheckedAt = null,
    globalObject,
} = {}) {
    const cacheStorage = getCacheStorage(globalObject);

    if (!cacheStorage || typeof cacheStorage.open !== 'function' || typeof Response === 'undefined') {
        return false;
    }

    try {
        const cache = await cacheStorage.open(PLANT_WATCH_CACHE_NAME);

        await cache.put(
            PLANT_WATCH_STATE_URL,
            new Response(
                // `isMuted` travels with the list because the worker has no other
                // way to learn it: a `periodicsync` wake-up reads this record and
                // nothing else, and a muted device must stay silent there too.
                JSON.stringify({ localeCode, seenPlantIds: parsePlantIds(seenPlantIds), isMuted, lastCheckedAt }),
                { headers: { 'Content-Type': 'application/json' } },
            ),
        );

        return true;
    } catch (error) {
        return false;
    }
}
